/**
 * Swiss QR-bill utilities for invoice PDF generation.
 *
 * Payload validation and QR generation use our vendored `swissqrbill` core,
 * which implements the Swiss Payment Standards and required Swiss cross.
 */
import {
  calculateSCORReferenceChecksum,
  formatReference,
  generateQRCodeGeometry,
  isSCORReferenceValid,
  type Data as SwissQRBillData,
  type QRCodeRect,
} from "@/vendor/swissqrbill";

const COMPANY = {
  name: "livemotion gmbh",
  street: "Bolacker",
  buildingNumber: "1",
  postalCode: "4563",
  city: "Gerlafingen",
  country: "CH",
  iban: "CH3180808008698321674",
};

export interface StructuredAddress {
  street: string;
  buildingNumber?: string;
  postalCode: string;
  city: string;
  country: string;
}

export interface QRBillParams {
  amount: number;
  reference: string;
  debtorName?: string;
  /** Free-form address as stored on the company, usually two or more lines. */
  debtorAddress?: string;
  additionalInfo?: string;
}

export type SwissQRRect = QRCodeRect;

function singleLine(value: string): string {
  return value.replace(/[\r\n\t ]+/g, " ").trim();
}

/**
 * Convert the existing free-form invoice address to the structured address
 * required by Swiss QR-bill IG 2.3.
 *
 * Expected shape:
 *   Badenerstrasse 90
 *   8952 Schlieren
 *
 * If it cannot be parsed safely, the debtor is omitted from the QR payload.
 * The debtor group is optional; emitting an incomplete group is invalid.
 */
export function parseStructuredAddress(
  value?: string,
  defaultCountry = "CH",
): StructuredAddress | undefined {
  if (!value) return undefined;

  const lines = value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2) return undefined;

  let country = defaultCountry.toUpperCase();
  if (/^[A-Z]{2}$/.test(lines.at(-1) ?? "")) {
    country = lines.pop()!;
  }

  const locality = lines.pop();
  const localityMatch = locality?.match(
    /^(?:([A-Z]{2})-)?([A-Z0-9][A-Z0-9-]{1,15})\s+(.+)$/u,
  );
  if (!localityMatch || lines.length === 0) return undefined;

  if (localityMatch[1]) country = localityMatch[1];

  const streetLine = lines.pop()!;
  const buildingMatch = streetLine.match(/^(.*\D)\s+(\d[\p{L}\p{N}./-]*)$/u);
  const street = singleLine(
    [...lines, buildingMatch?.[1] ?? streetLine].join(", "),
  );

  if (!street || !localityMatch[2] || !localityMatch[3]) return undefined;

  return {
    street,
    buildingNumber: buildingMatch?.[2],
    postalCode: localityMatch[2],
    city: singleLine(localityMatch[3]),
    country,
  };
}

/**
 * Generate an ISO 11649 creditor reference for the configured regular IBAN.
 * QRR references are only permitted with a QR-IBAN.
 */
export function generateQRReference(invoiceNumber: number): string {
  if (!Number.isSafeInteger(invoiceNumber) || invoiceNumber < 0) {
    throw new Error("Invoice number must be a non-negative integer");
  }

  const body = `INV${invoiceNumber}`;
  if (body.length > 21) {
    throw new Error("Invoice number is too long for a creditor reference");
  }

  return `RF${calculateSCORReferenceChecksum(body)}${body}`;
}

export function formatQRReference(reference: string): string {
  return formatReference(reference);
}

/** Replace legacy QRR values that are invalid with the configured regular IBAN. */
export function getInvoicePaymentReference(
  invoiceNumber: number,
  existingReference?: string | null,
): string {
  const normalized = existingReference?.replace(/\s/g, "").toUpperCase();
  return normalized && isSCORReferenceValid(normalized)
    ? normalized
    : generateQRReference(invoiceNumber);
}

/** Build and validate the data consumed by `swissqrbill`. */
export function createSwissQRBillData(params: QRBillParams): SwissQRBillData {
  if (!isSCORReferenceValid(params.reference)) {
    throw new Error("Invalid ISO 11649 creditor reference");
  }

  const debtorAddress = parseStructuredAddress(params.debtorAddress);
  const debtorName = params.debtorName
    ? singleLine(params.debtorName)
    : undefined;

  return {
    creditor: {
      account: COMPANY.iban,
      name: COMPANY.name,
      address: COMPANY.street,
      buildingNumber: COMPANY.buildingNumber,
      zip: COMPANY.postalCode,
      city: COMPANY.city,
      country: COMPANY.country,
    },
    amount: params.amount,
    currency: "CHF",
    reference: params.reference,
    ...(params.additionalInfo
      ? { message: singleLine(params.additionalInfo) }
      : {}),
    ...(debtorName && debtorAddress
      ? {
          debtor: {
            name: debtorName,
            address: debtorAddress.street,
            buildingNumber: debtorAddress.buildingNumber,
            zip: debtorAddress.postalCode,
            city: debtorAddress.city,
            country: debtorAddress.country,
          },
        }
      : {}),
  };
}

/**
 * Generate the standards-compliant vector geometry used by React-PDF.
 * The vendored `swissqrbill` core validates the data before returning the
 * QR modules and Swiss cross as 46 mm renderer-neutral rectangles.
 */
export function generateSwissQRCode(params: QRBillParams): SwissQRRect[] {
  return generateQRCodeGeometry(createSwissQRBillData(params));
}

export function getCompanyInfo() {
  return COMPANY;
}
